import { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Stack,
  alpha,
  useTheme
} from '@mui/material';
import PhotoIcon from '@mui/icons-material/Photo';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

interface ImageEngagementData {
  name: string;
  engagementTimeMs: number;
  url: string;
}

interface ImageEngagementGalleryProps {
  images: ImageEngagementData[];
  height?: number;
  showRanking?: boolean;
  emptyStateMessage?: string;
}

const ImageEngagementGallery = ({ 
  images,
  height = 120,
  showRanking = true,
  emptyStateMessage = "No image engagement data available yet"
}: ImageEngagementGalleryProps) => {
  const theme = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);

  if (images.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        height: height,
        bgcolor: alpha(theme.palette.grey[500], 0.04), 
        borderRadius: 2,
        border: `1px dashed ${alpha(theme.palette.grey[400], 0.3)}`
      }}>
        <Stack alignItems="center" spacing={1}>
          <PhotoIcon sx={{ fontSize: 40, color: alpha(theme.palette.grey[500], 0.5) }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {emptyStateMessage}
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Check if we need to show the navigation arrows
  useEffect(() => {
    const checkScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollWidth, clientWidth, scrollLeft } = scrollContainerRef.current;
        setShowRightArrow(scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth - 10);
        setShowLeftArrow(scrollLeft > 10);
      }
    };
    
    checkScroll();
    window.addEventListener('resize', checkScroll);
    
    return () => {
      window.removeEventListener('resize', checkScroll);
    };
  }, [images]);

  // Update arrow visibility when scrolling
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollWidth, clientWidth, scrollLeft } = scrollContainerRef.current;
      setShowRightArrow(scrollWidth > clientWidth && scrollLeft < scrollWidth - clientWidth - 10);
      setShowLeftArrow(scrollLeft > 10);
    }
  };
  
  // Scroll right when right arrow is clicked
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const clientWidth = scrollContainerRef.current.clientWidth;
      scrollContainerRef.current.scrollTo({
        left: currentScroll + (clientWidth * 0.8),
        behavior: 'smooth'
      });
    }
  };
  
  // Scroll left when left arrow is clicked
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const clientWidth = scrollContainerRef.current.clientWidth;
      scrollContainerRef.current.scrollTo({
        left: Math.max(0, currentScroll - (clientWidth * 0.8)),
        behavior: 'smooth'
      });
    }
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          py: 1,
          px: 1,
          '&::-webkit-scrollbar': {
            display: 'none', // Hide scrollbar for Chrome/Safari
          },
          scrollbarWidth: 'none', // Hide scrollbar for Firefox
          msOverflowStyle: 'none', // Hide scrollbar for IE/Edge
          gap: 2,
        }}
      >
        {images.map((image, index) => (
          <Box
            key={index}
            sx={{
              minWidth: height * 1.4, // Width proportional to height
              height: height,
              borderRadius: 1,
              overflow: 'hidden',
              flexShrink: 0,
              boxShadow: 1,
              cursor: 'pointer',
              transition: 'transform 0.2s',
              position: 'relative',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: 3,
              }
            }}
          >
            <img
              src={image.url}
              alt={`Image ${index + 1}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              onError={(e) => {
                // Fallback for broken images
                e.currentTarget.style.display = 'none';
                const parent = e.currentTarget.parentElement;
                if (parent) {
                  parent.innerHTML = `
                    <div style="
                      width: 100%; 
                      height: 100%; 
                      display: flex; 
                      align-items: center; 
                      justify-content: center;
                      background: ${alpha(theme.palette.grey[500], 0.1)};
                    ">
                      <svg style="
                        width: 32px; 
                        height: 32px; 
                        color: ${theme.palette.grey[600]};
                      " viewBox="0 0 24 24" fill="currentColor">
                        <path d="M21,19V5c0,-1.1 -0.9,-2 -2,-2H5c-1.1,0 -2,0.9 -2,2v14c0,1.1 0.9,2 2,2h14c1.1,0 2,-0.9 2,-2zM8.5,13.5l2.5,3.01L14.5,12l4.5,6H5l3.5,-4.5z"/>
                      </svg>
                    </div>
                  `;
                }
              }}
            />
            
            {/* Engagement Time Overlay - COMMENTED OUT FOR NOW */}
            {/* {showEngagementTime && (
              <Box sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                bgcolor: 'rgba(0, 0, 0, 0.6)',
                borderRadius: '50%',
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography 
                  variant="caption" 
                  sx={{
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.7rem',
                    lineHeight: 1
                  }}
                >
                  {Math.round(image.engagementTimeMs / 1000)}s
                </Typography>
              </Box>
            )} */}
            
            {/* Ranking Badge */}
            {showRanking && (
              <Box sx={{
                position: 'absolute',
                top: 4,
                left: 4,
                bgcolor: theme.palette.primary.main,
                color: 'white',
                width: 20,
                height: 20,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7rem',
                fontWeight: 'bold'
              }}>
                {index + 1}
              </Box>
            )}
          </Box>
        ))}
      </Box>
      
      {showLeftArrow && (
        <IconButton
          onClick={scrollLeft}
          disableRipple
          sx={{
            position: 'absolute',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: 'transparent !important',
            padding: 0,
            border: 'none',
            boxShadow: 'none',
            zIndex: 1,
            '&:hover, &:active, &:focus, &.Mui-focusVisible, &::before, &::after': {
              backgroundColor: 'transparent !important',
              outline: 'none',
            },
            '& .MuiSvgIcon-root': {
              filter: 'drop-shadow(0px 2px 3px rgba(0,0,0,0.2))',
              transition: 'transform 0.2s',
              color: theme.palette.grey[700]
            },
            '&:hover .MuiSvgIcon-root': {
              transform: 'scale(1.1)',
            }
          }}
        >
          <ArrowBackIosIcon fontSize="large" />
        </IconButton>
      )}
      
      {showRightArrow && (
        <IconButton
          onClick={scrollRight}
          disableRipple
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            backgroundColor: 'transparent !important',
            padding: 0,
            border: 'none',
            boxShadow: 'none',
            zIndex: 1,
            '&:hover, &:active, &:focus, &.Mui-focusVisible, &::before, &::after': {
              backgroundColor: 'transparent !important',
              outline: 'none',
            },
            '& .MuiSvgIcon-root': {
              filter: 'drop-shadow(0px 2px 3px rgba(0,0,0,0.2))',
              transition: 'transform 0.2s',
              color: theme.palette.grey[700]
            },
            '&:hover .MuiSvgIcon-root': {
              transform: 'scale(1.1)',
            }
          }}
        >
          <ArrowForwardIosIcon fontSize="large" />
        </IconButton>
      )}
    </Box>
  );
};

export default ImageEngagementGallery;